import { IDEOLOGY_AXES } from '@/constants/ideology'
import { cn } from '@/lib/utils'
import type { IdeologyVector } from '@/types'

interface SalienceDisplayProps {
  salience: IdeologyVector
  /** Compact mode for smaller displays */
  compact?: boolean
  className?: string
}

/**
 * Get importance label based on salience weight
 */
function getImportanceLabel(weight: number): string {
  if (weight >= 0.8) return 'Very Important'
  if (weight >= 0.6) return 'Important'
  if (weight >= 0.4) return 'Moderate'
  if (weight >= 0.2) return 'Low Priority'
  return 'Not Important'
}

/**
 * Visual representation of salience weights for each ideology axis.
 * Shows how much each issue matters to a cohort.
 */
export function SalienceDisplay({
  salience,
  compact = false,
  className,
}: SalienceDisplayProps) {
  // Sort by salience weight (highest first) for easier reading
  const sortedAxes = IDEOLOGY_AXES.map((axis, index) => ({
    axis,
    index,
    weight: salience[index],
  })).sort((a, b) => b.weight - a.weight)

  return (
    <div className={cn('space-y-2', compact && 'space-y-1.5', className)}>
      {sortedAxes.map(({ axis, weight }) => (
        <SalienceRow
          key={axis.key}
          label={axis.label}
          weight={weight}
          compact={compact}
        />
      ))}
    </div>
  )
}

interface SalienceRowProps {
  label: string
  weight: number
  compact: boolean
}

function SalienceRow({ label, weight, compact }: SalienceRowProps) {
  const importanceLabel = getImportanceLabel(weight)

  // Number of filled dots (out of 10)
  const filledDots = Math.round(weight * 10)

  // Color based on importance
  const getColor = (w: number) => {
    if (w >= 0.8) return 'bg-emerald-500'
    if (w >= 0.6) return 'bg-emerald-400'
    if (w >= 0.4) return 'bg-yellow-400'
    if (w >= 0.2) return 'bg-orange-400'
    return 'bg-gray-300'
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs w-24 truncate" title={label}>
          {label}
        </span>
        <div className="flex gap-0.5 flex-1">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1.5 flex-1 rounded-full',
                i < filledDots ? getColor(weight) : 'bg-muted'
              )}
            />
          ))}
        </div>
        <span className="text-xs text-muted-foreground w-8 text-right tabular-nums">
          {weight.toFixed(1)}
        </span>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">
          {importanceLabel}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {/* Dot indicator */}
        <div className="flex gap-1">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'w-2 h-2 rounded-full',
                i < filledDots ? getColor(weight) : 'bg-muted'
              )}
            />
          ))}
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">
          {weight.toFixed(2)}
        </span>
      </div>
    </div>
  )
}

/**
 * Very compact salience indicator showing top priorities
 */
interface SalienceMiniProps {
  salience: IdeologyVector
  /** Number of top priorities to show */
  count?: number
  className?: string
}

export function SalienceMini({ salience, count = 3, className }: SalienceMiniProps) {
  // Get top N priorities
  const topPriorities = IDEOLOGY_AXES.map((axis, index) => ({
    axis,
    weight: salience[index],
  }))
    .sort((a, b) => b.weight - a.weight)
    .slice(0, count)

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {topPriorities.map(({ axis, weight }) => (
        <span
          key={axis.key}
          className={cn(
            'text-xs px-1.5 py-0.5 rounded-full',
            weight >= 0.7 && 'bg-emerald-100 text-emerald-700',
            weight >= 0.5 && weight < 0.7 && 'bg-yellow-100 text-yellow-700',
            weight < 0.5 && 'bg-gray-100 text-gray-700'
          )}
          title={`${axis.label}: ${weight.toFixed(2)}`}
        >
          {axis.label.split(' ')[0]}
        </span>
      ))}
    </div>
  )
}
